import PropTypes from 'prop-types';
import React, { useCallback, useState, memo } from 'react';
import classNames from 'classnames';
import { List, Map } from 'immutable';

import { WysiwygView } from '../../../helpers/WysiwygView';
import { Card } from '../../../helpers/Card';
import { IconButton } from '../../../helpers/IconButton';

import style from './style.module.css';

/**
 * Component is used to display 1 deal note.
 *
 * @param props {Object} Any extra props that allowed on <p> tag.
 * @param props.onMouseEnter {Function} OnMouseEnter callback. 1st arg is event. 2nd is deal note's eventId.
 * @param props.onMouseLeave {Function} OnMouseLeave callback. 1st arg is event. 2nd is deal note's eventId.
 * @param props.note {Immutable.Map} Map, that should describe note. #content {string} is note content.
 */
const ContactExecutiveDealNoteComponent = ({
  onEditEvent,
  onMouseEnter,
  onMouseLeave,
  note,
  className = '',
  ...rest
}) => {
  const isMarked = note.get('isMarked', false);
  const eventId = note.get('eventId');
  const isBoldContent = isMarked ? 'font-weight: bold;' : 'font-weight: normal;';

  const topContent = (
    <div className={style.note} onDoubleClick={event => onEditEvent(event, note.set('id', eventId))}>
      <strong>{note.get('date')}</strong> <span className="text-muted">{note.get('userName')}</span>
    </div>
  );
  const content = note.get('content', '');

  const wrapBoldContent = `<span style='${isBoldContent}'>${content}</span>`;

  return (
    <div
      {...rest}
      className={className}
      onMouseEnter={event => onMouseEnter(event, eventId)}
      onMouseLeave={event => onMouseLeave(event, eventId)}
    >
      {topContent}
      <WysiwygView html={wrapBoldContent} onDoubleClick={event => onEditEvent(event, note.set('id', eventId))} />
    </div>
  );
};

ContactExecutiveDealNoteComponent.propTypes = {
  note: PropTypes.instanceOf(Map).isRequired,
  onEditEvent: PropTypes.func.isRequired,
  onMouseEnter: PropTypes.func.isRequired,
  onMouseLeave: PropTypes.func.isRequired,
};

const ContactExecutiveDealNotes = props => {
  const [closed, setClosed] = useState(() => props.dealNotes.size === 0);

  const onClick = useCallback(() => {
    setClosed(prev => !prev);
  }, []);

  return ContactExecutiveDealNotesRender(props, { closed }, onClick);
};

/**
 * Component is used to display list of deal notes.
 *
 * @param props.onMouseEnter {Function} OnMouseEnter callback. 1st arg is event. 2nd is deal note's eventId.
 * @param props.onMouseLeave {Function} OnMouseLeave callback. 1st arg is event. 2nd is deal note's eventId.
 * @param props.dealNotes {Immutable.List} List of Maps, that should describe note. #content {string} is note content.
 * #[isMarked=false] {boolean} uf `true` append .text-bold class to element. #eventId is deal note's eventId.
 */
const ContactExecutiveDealNotesRender = (props, state, onClick) => {
  const { onEditEvent, onMouseEnter, onMouseLeave, dealNotes } = props;
  const initClosed = dealNotes.size === 0;

  if (state.closed === true) {
    return (
      <Card as="article" className={classNames(style.host, style.collapsed)} flush>
        <IconButton className={style.toggle} icon="chevron-right" onClick={onClick} title="Show notes" />
        <h3 className={style.title}>Notes</h3>
      </Card>
    );
  }

  const content = initClosed ? (
    <p> No rows to display </p>
  ) : (
    dealNotes.map(note => (
      <ContactExecutiveDealNote
        key={note.get('eventId')}
        note={note}
        onEditEvent={onEditEvent}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      />
    ))
  );

  return (
    <Card
      actions={<IconButton className={style.toggle} icon="chevron-left" onClick={onClick} title="Hide notes" />}
      as="article"
      className={style.host}
      title="Notes"
      flush
    >
      <section className={style.body} id="acc2">
        <div className={style.content}>{content}</div>
      </section>
    </Card>
  );
};

ContactExecutiveDealNotes.propTypes = {
  dealNotes: PropTypes.instanceOf(List).isRequired,
};

export const ContactExecutiveDealNote = memo(ContactExecutiveDealNoteComponent);
export default ContactExecutiveDealNotes;
